import { Themed } from "@client/styles/themes";
import React, { useEffect, useState } from "react";
import {
    Disclosure,
    DisclosureGroup,
    DisclosurePanel,
    Heading,
} from "react-aria-components";
import { t } from "ttag";

import {
    BasicLinkList,
    CmsFooterContent,
    FooterLogoBlock,
    FooterNav as FooterNavProps,
    FooterSocialBlock,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { Clickable } from "../Clickables";
import { RichText } from "../RichText";
import { SocialIcons } from "../SocialIcons";
import { NewsletterSignUp } from "./NewsletterSignUp";

import styles from "./Footer.module.scss";

/**
 * Render the cookie preferences link once Cookiebot has loaded
 */
const CookiePreferencesLink = () => {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        // Check if already loaded (like if user navigates back or whatever)
        if (window.Cookiebot) {
            setReady(true);
            return;
        }

        const handleReady = () => setReady(true);
        window.addEventListener("CookiebotOnConsentReady", handleReady);
        return () => {
            window.removeEventListener("CookiebotOnConsentReady", handleReady);
        };
    }, []);

    if (!ready) return null;

    return (
        <Clickable
            className={styles.link}
            onClick={() => window.Cookiebot?.renew()}
        >
            {t`Cookie Preferences`}
        </Clickable>
    );
};

const BasicLinks = ({
    value,
    appendCookieBot = false,
}: {
    value: BasicLinkList;
    appendCookieBot?: boolean;
}) => (
    <ul>
        <StreamField
            value={value}
            tagName={React.Fragment}
            components={{
                link: ({ value }) => (
                    <li>
                        <Clickable simpleLink={value} className={styles.link} />
                    </li>
                ),
                text: ({ value }) => (
                    <li>
                        <RichText value={value} className={styles.richText} />
                    </li>
                ),
            }}
        />
        <li>{appendCookieBot && <CookiePreferencesLink />}</li>
    </ul>
);

function Nav({ value }: { value: FooterNavProps }) {
    return (
        <nav>
            <DisclosureGroup allowsMultipleExpanded={false}>
                <ul className={styles.mobileNav}>
                    {value.map(({ value: panel, id }) => (
                        <li key={id} className={styles.navPanel}>
                            <Disclosure id={id}>
                                {(args) => (
                                    <>
                                        <Heading>
                                            <Clickable
                                                className={styles.expandButton}
                                                slot="trigger"
                                                icon={
                                                    args.isExpanded
                                                        ? "minus"
                                                        : "plus"
                                                }
                                            >
                                                {panel.header}
                                            </Clickable>
                                        </Heading>
                                        <DisclosurePanel>
                                            {panel.links && (
                                                <BasicLinks
                                                    value={panel.links}
                                                />
                                            )}
                                        </DisclosurePanel>
                                    </>
                                )}
                            </Disclosure>
                        </li>
                    ))}
                </ul>
            </DisclosureGroup>

            <ul className={styles.desktopNav}>
                {value.map(({ value: panel, id }) => (
                    <li key={id} className={styles.navPanel}>
                        <h3 className={styles.expandButton}>{panel.header}</h3>
                        {panel.links && <BasicLinks value={panel.links} />}
                    </li>
                ))}
            </ul>
        </nav>
    );
}

function Social({ value }: { value: FooterSocialBlock }) {
    return (
        <div className={styles.social}>
            <NewsletterSignUp
                successMessage={value.newsletter?.success_text ?? undefined}
            />
            <SocialIcons />
        </div>
    );
}

const Logo = ({ value }: { value: FooterLogoBlock }) =>
    value.image && (
        <div className={styles.logo}>
            <ImageChooserBlock value={value.image} />
        </div>
    );

function Legal({ value }: { value: BasicLinkList }) {
    return (
        <div className={styles.legal}>
            <BasicLinks value={value} appendCookieBot={true} />
        </div>
    );
}

export function Footer(props: { content: CmsFooterContent }) {
    return (
        <Themed theme="robinsBlue" tag="footer">
            <div className={styles.root}>
                <StreamField
                    value={props.content.value}
                    tagName={React.Fragment}
                    components={{
                        nav: Nav,
                        social: Social,
                        logo: Logo,
                        legal: Legal,
                    }}
                />
            </div>
        </Themed>
    );
}
